import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
  LinearProgress
} from '@mui/material';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import type { Warehouse } from '../types/inventory.types';

interface BulkWarehouseAssignDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (warehouseId: string) => Promise<void>;
  warehouses: Warehouse[];
  selectedCount: number;
  processing: boolean;
}

export const BulkWarehouseAssignDialog: React.FC<BulkWarehouseAssignDialogProps> = ({
  open,
  onClose,
  onConfirm,
  warehouses,
  selectedCount,
  processing
}) => {
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Modal açıldığında "Merkez Depo"yu otomatik seç
  useEffect(() => {
    if (open && warehouses.length > 0 && !selectedWarehouseId) {
      // "Merkez Depo" veya "Merkez" isminde bir depo ara
      const centralWarehouse = warehouses.find(w => 
        w.name.toLowerCase().includes('merkez') ||
        w.name.toLowerCase() === 'merkez depo' ||
        w.name.toLowerCase() === 'ana depo'
      );
      
      if (centralWarehouse) {
        setSelectedWarehouseId(centralWarehouse.id);
      } else {
        // Bulunamazsa ilk depoyu seç
        setSelectedWarehouseId(warehouses[0].id);
      }
    }
  }, [open, warehouses, selectedWarehouseId]);

  // Modal kapandığında state'i temizle
  useEffect(() => {
    if (!open) {
      setSelectedWarehouseId('');
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!selectedWarehouseId) {
      setError('Lütfen bir depo seçin');
      return;
    }

    setError(null);
    try {
      await onConfirm(selectedWarehouseId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
    }
  };

  const handleClose = () => {
    if (!processing) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={processing}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarehouseIcon color="primary" />
          <Typography variant="h6">
            Toplu Depo Atama
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {processing && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              Ürünler depoya atanıyor...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>{selectedCount}</strong> ürün seçili depoya atanacak.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          ⚠️ <strong>Not:</strong> Ürünler başlangıç miktarı <strong>0 (sıfır)</strong> ile depoya atanacaktır. 
          Daha sonra depo transferi veya miktar güncelleme ile stok ekleyebilirsiniz.
        </Alert>

        <FormControl fullWidth disabled={processing}>
          <InputLabel id="warehouse-select-label">Depo Seçin</InputLabel>
          <Select
            labelId="warehouse-select-label"
            id="warehouse-select"
            value={selectedWarehouseId}
            label="Depo Seçin"
            onChange={(e) => setSelectedWarehouseId(e.target.value)}
          >
            {warehouses.map((warehouse) => (
              <MenuItem key={warehouse.id} value={warehouse.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarehouseIcon fontSize="small" />
                  {warehouse.name}
                  {warehouse.name.toLowerCase().includes('merkez') && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        ml: 1, 
                        px: 1, 
                        py: 0.25, 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        borderRadius: 1 
                      }}
                    >
                      Önerilen
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {warehouses.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Hiç depo bulunamadı. Lütfen önce bir depo oluşturun.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={processing}
          color="inherit"
        >
          İptal
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={processing || !selectedWarehouseId || warehouses.length === 0}
          startIcon={processing ? <CircularProgress size={20} /> : <WarehouseIcon />}
        >
          {processing ? 'Atanıyor...' : 'Depoya Ata'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

